import type { SdkFunction } from "@product/scmp-sdk";
import { useAtomValue } from "jotai";
import { useRouter } from "next/router";
import { useCallback, useEffect, useMemo } from "react";
import type { Environment } from "react-relay";

import { sendMirrorCustomEvent } from "shared/components/tracking/mirror";
import { config, newsletter } from "shared/data";
import { accountAtom } from "shared/lib/account";

import { useBookmark } from "scmp-app/components/content/content-bookmark/hooks";
import { useUserFollow } from "scmp-app/components/follow-button/hooks";
import { useLoginDialogStateHelper } from "scmp-app/components/login-dialog/hooks";
import {
  registerMixPanelParameters,
  trackMixPanel,
  useMixpanel,
} from "scmp-app/components/mixpanel";
import { gdprAtom } from "scmp-app/components/tracking/gdpr";
import { sendGA4Tracking } from "scmp-app/components/tracking/google-analytics-4/apis";
import { sendGTMTracking } from "scmp-app/components/tracking/google-tag-manager/apis";
import { appAtom } from "scmp-app/lib/app/atoms";
import { useCurrentArticleIds } from "scmp-app/lib/current-item";
import { useEdition } from "scmp-app/lib/edition";
import { useFetchUserTypes, useMyNews } from "scmp-app/lib/hooks";
import { rosettaAtom } from "scmp-app/lib/rosetta";
import {
  useCurrentPageType,
  useGetHomeAsPath,
  useRouteHandler,
  useRouterReplaceWithSanitizedKeys,
} from "scmp-app/lib/router/hooks";

import { useHasWhyRegisterPPTOHomepagePositionIntersected } from "./has-why-register-ppto-homepage-position-intersected";

export const useScmpSdk = (environment: Environment) => {
  const { asPath, pathname, query } = useRouter();
  const router = useRouter();
  const replace = useRouterReplaceWithSanitizedKeys();
  const handleRoutePush_ = useRouteHandler();
  const { currentArticleId, currentArticleUuid } = useCurrentArticleIds();

  const edition = useEdition();
  const currentPageType = useCurrentPageType();
  const { checkIsFollowed, handleFollowAction } = useUserFollow();
  const { checkIsBookmarked, handleBookmarkAction } = useBookmark();
  const { hasEnoughFollowedItems, initialHasEnoughFollowedItems } = useMyNews();
  const { openLoginDialog } = useLoginDialogStateHelper();
  const mixpanel = useMixpanel();
  const { fetchUserTypes, userTypes } = useFetchUserTypes();
  const userTypeIds = userTypes.map(userType => userType.id);
  useEffect(() => {
    void fetchUserTypes();
  }, [fetchUserTypes]);

  const { navigatorInfo } = useAtomValue(appAtom);
  const { user } = useAtomValue(accountAtom);
  const asyncRosettaState = useAtomValue(rosettaAtom);
  const { isAccepted: hasAcceptedGDPR } = useAtomValue(gdprAtom);

  const hasIpAccess = asyncRosettaState?.result?.hasIpAccess ?? false;
  const isScmpSubscriber = asyncRosettaState?.result?.user?.isSCMPSubscriber ?? false;
  const isSubscriptionEnabled =
    asyncRosettaState?.result?.contentServiceUserInfo?.isSubscriptionEnabled ?? false;
  const isShowSubscription = !hasIpAccess && !isScmpSubscriber && isSubscriptionEnabled;

  const bookmarkCheck = useCallback<SdkFunction["bookmark"]["check"]>(
    payload => checkIsBookmarked(payload.entityId),
    [checkIsBookmarked],
  );
  const bookmarkUpdate = useCallback<SdkFunction["bookmark"]["update"]>(
    payload =>
      handleBookmarkAction(
        { action: payload.action, id: payload.entityId },
        { ga4Events: payload.trackings.ga4Events },
      ),
    [handleBookmarkAction],
  );
  const handleCheckIsFollowed = useCallback<SdkFunction["handleCheckIsFollowed"]>(
    payload => checkIsFollowed(payload.followType, payload.entityId),
    [checkIsFollowed],
  );
  const handleFollow = useCallback<SdkFunction["handleFollow"]>(
    payload => {
      handleFollowAction(payload.entityIds, payload.followType, payload.action, payload.callback);
    },
    [handleFollowAction],
  );

  const handleOpenLoginDialog = useCallback<SdkFunction["handleOpenLoginDialog"]>(
    ({ description, destination, ga4CustomParameter, title }) => {
      openLoginDialog({ description, destination, ga4CustomParameter, title });
    },
    [openLoginDialog],
  );
  const handleRegisterMixPanel = useCallback<SdkFunction["handleRegisterMixPanel"]>(
    payload => {
      if (!payload || !mixpanel) return;
      registerMixPanelParameters(mixpanel.result, payload);
    },
    [mixpanel],
  );
  const handleRemoveRouteQuery = useCallback<SdkFunction["handleRemoveRouteQuery"]>(
    async sanitizedKeys => {
      await replace(
        sanitizedKeys,
        { pathname, query },
        {
          shallow: true,
        },
      );
    },
    [pathname, query, replace],
  );
  const handleRoutePush = useCallback<SdkFunction["handleRoutePush"]>(
    async (url: string, extraOptions = {}) => {
      await handleRoutePush_(url, extraOptions);
    },
    [handleRoutePush_],
  );
  const handleTrackGA4 = useCallback<SdkFunction["handleTrackGA4"]>(payload => {
    if (!payload) return;
    sendGA4Tracking(payload, { untyped: true });
  }, []);
  const handleTrackGTM = useCallback<SdkFunction["handleTrackGTM"]>(payload => {
    if (!payload) return;
    sendGTMTracking(payload, { untyped: true });
  }, []);
  const handleTrackMixPanel = useCallback<SdkFunction["handleTrackMixPanel"]>(
    payload => {
      if (!payload || !mixpanel) return;
      trackMixPanel(mixpanel.result, payload, { untyped: true });
    },
    [mixpanel],
  );

  const handleTrackMirror = useCallback<SdkFunction["handleTrackMirror"]>(payload => {
    if (!payload) return;
    sendMirrorCustomEvent(payload, { untyped: true });
  }, []);

  const homeAsPath = useGetHomeAsPath();

  const hasWhyRegisterPPTOHomepagePositionIntersected =
    useHasWhyRegisterPPTOHomepagePositionIntersected();

  const value = useMemo<SdkFunction>(
    () => ({
      bookmark: {
        check: bookmarkCheck,
        update: bookmarkUpdate,
      },
      config: {
        accounts: {
          authCallbackUrl: config.account.authCallbackUrl,
          host: config.account.scmpAccountHost,
          registerCallbackUrl: config.account.registerCallbackUrl,
        },
        corporateSubscriber: {
          host: config.corporateSubscriber.host,
        },
        environment: "dev",
        facebook: {
          appId: config.social.facebook.appId,
          likeUrl: config.social.facebook.likeUrl,
        },
        graphql: {
          apiKey: config.graphql.contentService.apiKey,
          host: config.graphql.contentService.host,
        },
        mixpanel: { token: config.account.mixPanelToken },
        newsletter: {
          globalImpact: newsletter.globalImpact.entityId,
          lunar: newsletter.lunar.entityId,
          postMag: newsletter.postMag.entityId,
          thisWeekInAsia: newsletter.thisWeekInAsia.entityId,
        },
        notification: {
          host: "",
        },
        readingHistory: {
          apiKey: config.graphql.contentService.apiKey,
          host: config.general.readingHistoryHost,
        },
        recaptcha: {
          siteKey: config.account.recaptchaSiteKey,
        },
        siteUrl: config.general.siteUrl,
        subscribe: {
          host: config.rosetta.subscribeHost,
        },
        twitter: {
          accountName: config.social.twitter.scmp.accountName,
        },
      },
      currentArticleId,
      currentArticleUuid,
      currentPageType,
      currentRoute: asPath,
      edition,
      environment,
      handleCheckIsFollowed,
      handleFollow,
      handleOpenLoginDialog,
      handleRegisterMixPanel,
      handleRemoveRouteQuery,
      handleRoutePush,
      handleTrackGA4,
      handleTrackGTM,
      handleTrackMirror,
      handleTrackMixPanel,
      hasAcceptedGDPR,
      hasEnoughFollowedItems,
      hasWhyRegisterPPTOHomepagePositionIntersected,
      homeAsPath,
      initialHasEnoughFollowedItems,
      isShowSubscription,
      rosettaInstance: asyncRosettaState?.result?.instance,
      router,
      user: user ?? undefined,
      userCountry: navigatorInfo?.result?.country,
      userTypeIds,
    }),
    [
      asPath,
      asyncRosettaState?.result?.instance,
      bookmarkCheck,
      bookmarkUpdate,
      currentArticleId,
      currentArticleUuid,
      currentPageType,
      edition,
      environment,
      handleCheckIsFollowed,
      handleFollow,
      handleOpenLoginDialog,
      handleRegisterMixPanel,
      handleRemoveRouteQuery,
      handleRoutePush,
      handleTrackGA4,
      handleTrackGTM,
      handleTrackMixPanel,
      handleTrackMirror,
      hasAcceptedGDPR,
      hasEnoughFollowedItems,
      hasWhyRegisterPPTOHomepagePositionIntersected,
      homeAsPath,
      initialHasEnoughFollowedItems,
      navigatorInfo?.result?.country,
      isShowSubscription,
      router,
      user,
      userTypeIds,
    ],
  );

  return value;
};
